/**
 * The Detail — Brand Equity Visualization / Flow (SYR-68, Phase E).
 *
 * Reuses the existing orbital V3 visualization wrapper (per the brand decision
 * to keep V3 as-is on our colours), driven by the global MarketerScope. Just
 * re-housed in the new shell chrome.
 */

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Modal, Grid } from 'antd';
import { ExpandOutlined, CloseOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../../hooks/useAuth';
import BrandEquityVisualizationWrapper from '../../BrandEquity/BrandEquityVisualizationWrapper';
import { useScopedBrandEquity } from '../useScopedBrandEquity';
import { useMarketerScope } from '../scope/MarketerScopeContext';
import { MarketerPageHeader } from './MarketerPageHeader';
import { MarketerLoading } from '../atoms/MarketerLoading';
import { MarketerResponsesNeeded } from '../components/MarketerResponsesNeeded';
import { THEME } from '../config/theme';

export const DetailPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { user } = useAuth();
  const { activeBrand } = useMarketerScope();
  // withAiSummaries: this is the only destination that renders the element
  // panel's "First Thoughts", and the scope payload alone never asks for them.
  const { response, isError, error } = useScopedBrandEquity({
    withAiSummaries: true,
  });
  const [searchParams] = useSearchParams();
  const initialElement = searchParams.get('element');
  const [fullPage, setFullPage] = useState(false);

  // Not enough responses yet — show the shared "More Responses Needed" card
  // (with "Finish setup" while onboarding is incomplete), consistent with the
  // other destinations, instead of an empty orbital.
  const insufficient = !!response?.insufficientSampleSize;

  return (
    <div>
      <MarketerPageHeader
        title={
          <FormattedMessage
            id="marketerView.detail.title"
            defaultMessage="Brand Equity Visualization"
          />
        }
        subtitle={
          <FormattedMessage
            id="marketerView.detail.sub"
            defaultMessage="Visual representation of your brand equity flow and analysis."
          />
        }
        actions={
          response && !insufficient ? (
            <Button
              icon={<ExpandOutlined />}
              onClick={() => setFullPage(true)}
              style={{
                background: '#fff',
                border: `1px solid ${THEME.border}`,
                color: THEME.textBase,
                fontWeight: 600,
                borderRadius: 10,
              }}
            >
              <FormattedMessage
                id="marketerView.detail.fullPage"
                defaultMessage="Full page"
              />
            </Button>
          ) : undefined
        }
      />
      {insufficient ? (
        <MarketerResponsesNeeded
          totalResponses={response?.totalResponses ?? 0}
          fromLabel={intl.formatMessage({
            id: 'marketerView.nav.detail',
            defaultMessage: 'The Detail',
          })}
          description={intl.formatMessage(
            {
              id: 'marketerView.detail.insufficient',
              defaultMessage:
                'The brand equity visualization needs at least 10 survey responses. You currently have {count} response{plural}.',
            },
            {
              count: response?.totalResponses ?? 0,
              plural: (response?.totalResponses ?? 0) === 1 ? '' : 's',
            },
          )}
        />
      ) : (
        <>
      {/* No card chrome — the orbital sits directly on the page. The V3 canvas
          sizes to its parent's clientHeight, so the container needs an explicit
          height. Fill the available viewport height (min 820) so the full radial
          graph fits without clipping the outer ring top/bottom. */}
      <div
        style={{
          height: isMobile ? 'calc(100vh - 160px)' : 'calc(100vh - 200px)',
          minHeight: isMobile ? 460 : 820,
          width: '100%',
          position: 'relative',
        }}
      >
        {!response ? (
          // Until data arrives, show the spinner — and only the "Could not load"
          // Alert on a genuine error. This avoids the Alert flashing on the
          // initial mount (no response yet) when arriving from the drawer.
          isError ? (
            <Alert
              type="error"
              message={
                (error as { data?: { message?: string } })?.data?.message ?? (
                  <FormattedMessage
                    id="marketerView.error.loadFailed"
                    defaultMessage="Could not load brand equity data."
                  />
                )
              }
            />
          ) : (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MarketerLoading />
            </div>
          )
        ) : (
          <BrandEquityVisualizationWrapper
            results={response}
            isModal={false}
            isAdmin={false}
            organisationId={user?.organisation?.id}
            brandId={activeBrand?.id}
            forceVisualizationType="orbital-v3"
            initialSelectedElement={initialElement}
          />
        )}
      </div>

      {/* Full-page modal — same orbital, edge-to-edge (mirrors the old viz page). */}
      <style>{`
        .marketer-fullpage-modal .ant-modal-wrap { overflow: hidden !important; }
        .marketer-fullpage-modal .ant-modal {
          overflow: hidden !important;
          max-width: 100vw !important;
          padding: 0 !important;
        }
      `}</style>
      <Modal
        title={null}
        open={fullPage}
        onCancel={() => setFullPage(false)}
        footer={null}
        width="100vw"
        closable
        closeIcon={
          <CloseOutlined
            style={{
              fontSize: 18,
              padding: 8,
              borderRadius: 6,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              color: '#ffffff',
            }}
          />
        }
        style={{ top: 0, left: 0, right: 0, bottom: 0, margin: 0, maxWidth: 'none' }}
        styles={{
          body: { height: '100vh', padding: 0, overflow: 'hidden' },
          content: { height: '100vh', borderRadius: 0, padding: 0, overflow: 'hidden' },
          header: {
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1000,
            background: 'transparent',
            border: 'none',
            padding: 0,
            margin: 0,
          },
          mask: { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
        }}
        centered={false}
        rootClassName="marketer-fullpage-modal"
        destroyOnClose
      >
        {response && (
          <div style={{ height: '100%', width: '100%', overflow: 'hidden' }}>
            <BrandEquityVisualizationWrapper
              results={response}
              isModal={true}
              isAdmin={false}
              organisationId={user?.organisation?.id}
              brandId={activeBrand?.id}
              forceVisualizationType="orbital-v3"
              initialSelectedElement={initialElement}
            />
          </div>
        )}
      </Modal>
        </>
      )}
    </div>
  );
};
